import { APP } from '../tokens';
import type { GeneratingStep, FoundItem } from '../../../types/review';

const F = APP.fontFamily;

// ─── Icons ──────────────────────────────────────

function CompletedIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={APP.textReviewAccepted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0, animation: 'review-spin 1s linear infinite' }}>
      <circle
        cx="7"
        cy="7"
        r="5.5"
        stroke={APP.textReviewPriorityHigh}
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="26"
        strokeDashoffset="8"
        fill="none"
      />
      <style>{`@keyframes review-spin { to { transform: rotate(360deg); } }`}</style>
    </svg>
  );
}

function PendingCircle() {
  return (
    <svg width={14} height={14} viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
      <circle cx="7" cy="7" r="5.5" stroke="#E8DED4" strokeWidth="1.5" fill="none" />
    </svg>
  );
}

function ErrorStepIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#C4654A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <circle cx="12" cy="12" r="10" />
      <line x1="15" y1="9" x2="9" y2="15" />
      <line x1="9" y1="9" x2="15" y2="15" />
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#C4654A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

// ─── Props ──────────────────────────────────────

interface ReviewGeneratingPanelProps {
  steps: GeneratingStep[];
  foundItems: FoundItem[];
  progress: number;
  error?: string | null;
  onRetry?: () => void;
  onRunOCR?: () => void;
}

// ─── Component ──────────────────────────────────

export default function ReviewGeneratingPanel({ steps, foundItems, progress, error, onRetry, onRunOCR }: ReviewGeneratingPanelProps) {
  const hasError = !!error;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflowY: 'auto' }}>
      {/* ─── Summary Strip ──────────────────── */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          paddingBlock: 14,
          paddingInline: 16,
          gap: 10,
          borderBottom: `1px solid ${APP.borderReviewCardDefault}`,
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: hasError ? '#C4654A' : APP.textReviewMuted,
            lineHeight: '16px',
            fontFamily: F,
          }}
        >
          {hasError ? 'Generation failed' : 'Analyzing document...'}
        </span>

        {/* Progress bar */}
        <div
          style={{
            width: '100%',
            height: 4,
            borderRadius: 2,
            backgroundColor: hasError ? '#F0DDD5' : APP.bgReviewProgress,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: `${progress}%`,
              height: 4,
              borderRadius: 2,
              backgroundColor: hasError ? '#C4654A' : APP.textReviewPriorityHigh,
              transition: 'width 0.4s ease',
            }}
          />
        </div>
      </div>

      {/* ─── Analysis Steps ─────────────────── */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          paddingBlock: 16,
          paddingInline: 16,
          gap: 12,
        }}
      >
        {steps.map((step) => {
          const isPendingAfterError = hasError && step.status === 'pending';
          return (
            <div
              key={step.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                opacity: isPendingAfterError ? 0.4 : 1,
              }}
            >
              {step.status === 'completed' && <CompletedIcon />}
              {step.status === 'active' && <SpinnerIcon />}
              {step.status === 'error' && <ErrorStepIcon />}
              {step.status === 'pending' && <PendingCircle />}
              <span
                style={{
                  fontSize: 12,
                  fontWeight: step.status === 'active' || step.status === 'error' ? 500 : 400,
                  color:
                    step.status === 'completed'
                      ? APP.textCardDetail
                      : step.status === 'active'
                        ? APP.textReviewPriorityHigh
                        : step.status === 'error'
                          ? '#C4654A'
                          : '#B8A99A',
                  lineHeight: '16px',
                  fontFamily: F,
                }}
              >
                {step.label}
              </span>
            </div>
          );
        })}

        {/* ─── Error Card ────────────────────── */}
        {hasError && (
          <>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                marginTop: 8,
                borderRadius: 8,
                paddingBlock: 14,
                paddingInline: 14,
                gap: 8,
                backgroundColor: '#FDF5F3',
                border: '1px solid #F0D5CE',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <WarningIcon />
                <span style={{ fontSize: 12, fontWeight: 600, color: '#C4654A', lineHeight: '16px', fontFamily: F }}>
                  Unable to complete analysis
                </span>
              </div>
              <span style={{ fontSize: 11, fontWeight: 400, color: '#8B5E4B', lineHeight: '17px', fontFamily: F }}>
                {error}
              </span>
            </div>

            {/* ─── Action Buttons ─────────────── */}
            <div style={{ display: 'flex', alignItems: 'center', marginTop: 4, gap: 8 }}>
              <button
                type="button"
                onClick={onRetry}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexGrow: 1,
                  flexShrink: 1,
                  flexBasis: '0%',
                  borderRadius: 6,
                  paddingBlock: 8,
                  backgroundColor: APP.textDark,
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: F,
                  fontSize: 12,
                  fontWeight: 500,
                  color: '#FDFCFB',
                  lineHeight: '16px',
                }}
              >
                Try Again
              </button>
              <button
                type="button"
                onClick={onRunOCR}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 6,
                  paddingBlock: 8,
                  paddingInline: 14,
                  backgroundColor: 'transparent',
                  border: `1px solid ${APP.borderReviewCardDefault}`,
                  cursor: 'pointer',
                  fontFamily: F,
                  fontSize: 12,
                  fontWeight: 500,
                  color: APP.textCardDetail,
                  lineHeight: '16px',
                }}
              >
                Run OCR First
              </button>
            </div>
          </>
        )}

        {/* ─── Found So Far ────────────────── */}
        {!hasError && foundItems.length > 0 && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              marginTop: 12,
              paddingTop: 12,
              gap: 6,
              borderTop: `1px solid ${APP.bgReviewProgress}`,
            }}
          >
            <span
              style={{
                fontSize: 10,
                fontWeight: 500,
                color: APP.textReviewMuted,
                lineHeight: '12px',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                fontFamily: F,
              }}
            >
              Found so far
            </span>

            {foundItems.map((item) => (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: 6,
                  paddingBlock: 8,
                  paddingInline: 10,
                  gap: 6,
                  backgroundColor: '#F8F6F3',
                  opacity: item.scanning ? 0.5 : 1,
                }}
              >
                <div
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    backgroundColor: item.scanning ? '#B8A99A' : APP.textReviewPriorityHigh,
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 400,
                    color: item.scanning ? APP.textReviewMuted : APP.textCardDetail,
                    lineHeight: '14px',
                    fontFamily: F,
                  }}
                >
                  {item.scanning
                    ? item.title
                    : `§${item.sectionRef} ${item.title}${item.description ? ` — ${item.description}` : ''}`}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
